import {
  cleanLinkedInText,
  cleanName,
  cleanProfessionalDisplayText,
} from "@/lib/utils/nameUtils";
import { extractLinkedInVanity } from "@/app/components/utils/linkedinUtils";

function getInitials(firstName, lastName) {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return `${first}${last}`.toUpperCase();
}

function parseFullName(fullName) {
  const words = fullName.trim().split(/\s+/);
  return {
    firstName: words[0] || "",
    lastName: words.slice(1).join(" ") || "",
  };
}

export const enrichByEmail = async (email) => {
  try {
    const response = await fetch("/api/search/enrich-by-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    const result = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: "No profile found for this email" };
      }
      throw new Error(result.error || "Email enrichment failed");
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const enrichByNameAndCompany = async (fullName, companyName = "") => {
  try {
    const nameParts = parseFullName(fullName);

    const response = await fetch("/api/search/enrich-by-name", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        companyName,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: "No profile found for this name" };
      }
      throw new Error(result.error || "Name enrichment failed");
    }

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const enrichByLinkedInUrl = async (linkedinUrl) => {
  try {
    const response = await fetch("/api/search/enrich-by-linkedin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ linkedinUrl }),
    });

    const result = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: "No profile found for this LinkedIn URL",
        };
      }
      throw new Error(result.error || "LinkedIn enrichment failed");
    }

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const transformEnrichToSearchResult = async (serverResponse) => {
  try {
    const { enrichData, salaryData, searchQuery, searchType } = serverResponse;

    const firstName = cleanName(enrichData?.person?.firstName) || "";
    const lastName = cleanName(enrichData?.person?.lastName) || "";
    const fullName = `${firstName} ${lastName}`.trim();

    let expertise = [];
    if (salaryData?.expertise) {
      if (Array.isArray(salaryData.expertise)) {
        expertise = salaryData.expertise.filter(
          (skill) => typeof skill === "string" && skill.trim(),
        );
      } else if (typeof salaryData.expertise === "string") {
        expertise = [salaryData.expertise];
      }
    }

    let vanity = null;
    const linkedinUrl = enrichData?.person?.linkedInUrl;
    if (linkedinUrl) {
      vanity = extractLinkedInVanity(linkedinUrl);
    }

    const linkedInId =
      enrichData?.person?.memberIdentifier ||
      enrichData?.person?.linkedInIdentifier ||
      enrichData?.person?.id ||
      null;

    const halfRate =
      salaryData?.sessionTypes?.["30min"]?.rate ||
      salaryData?.sessionTypes?.["30min"]?.estimatedRate ||
      null;

    const hourlyRate =
      salaryData?.sessionTypes?.["60min"]?.rate ||
      salaryData?.sessionTypes?.["60min"]?.estimatedRate ||
      null;

    const sessionTypes = {};
    if (hourlyRate) {
      sessionTypes["30min"] = {
        enabled: true,
        rate: halfRate,
      };
      sessionTypes["60min"] = {
        enabled: true,
        rate: hourlyRate,
      };
    }

    return {
      id: linkedInId,
      name: fullName,
      given_name: firstName,
      family_name: lastName,
      title: cleanProfessionalDisplayText(
        enrichData?.person?.currentPositionTitle ||
          cleanLinkedInText(enrichData?.person?.headline || "") ||
          "",
      ),
      company: enrichData?.company?.name || "",
      expertise: expertise,
      avatar: getInitials(firstName, lastName),
      picture: enrichData?.person?.picture || enrichData?.person?.photoUrl,
      country: enrichData?.person?.location?.country || "",
      countryCode: enrichData?.person?.location?.countryCode || "",
      companyUrl: enrichData?.company?.websiteUrl,
      linkedinUrl: linkedinUrl,
      vanity: vanity,
      hourlyRate: hourlyRate,
      halfRate: halfRate,
      acceptRate: salaryData?.acceptRate || null,
      enriched: true,
      enrichedData: enrichData,
      salaryData: salaryData,
      searchQuery: searchQuery,
      searchType: searchType,
      sessionTypes: sessionTypes,
    };
  } catch (error) {
    console.error("Error transforming enriched data:", error);
    throw error;
  }
};

export const saveEnrichedToPortfolios = async (
  enrichedResult,
  searchQuery,
  searchType,
) => {
  try {
    const response = await fetch("/api/search/enrich", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enrichedResult, searchQuery, searchType }),
    });

    const result = await response.json();

    if (!response.ok || result.success !== true) {
      throw new Error(result.error || "Failed to save enriched data");
    }

    return result;
  } catch (error) {
    console.error("Failed to save enriched data to portfolios:", error);
    throw error;
  }
};
